<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

type FaqItem = {
  q: string;
  a: string;
};

type FaqCategory = {
  key: string;
  label: string;
  questions: FaqItem[];
};

const { messages, locale } = useI18n();

const categories = computed<FaqCategory[]>(() => {
  const list = messages.value[locale.value]?.pages?.zakat?.faq?.categories;
  return Array.isArray(list) ? (list as FaqCategory[]) : [];
});

const activeCategoryKey = ref("");
const openQuestionIndex = ref<number | null>(null);

const activeCategory = computed(() => {
  const firstKey = categories.value[0]?.key ?? "";
  const key = activeCategoryKey.value || firstKey;
  return categories.value.find((c) => c.key === key) ?? categories.value[0];
});

function setCategory(key: string) {
  activeCategoryKey.value = key;
  openQuestionIndex.value = null;
}

function toggleQuestion(index: number) {
  openQuestionIndex.value = openQuestionIndex.value === index ? null : index;
}

function onEnter(el: Element) {
  const node = el as HTMLElement;
  node.style.height = "0px";
  node.style.opacity = "0";
  requestAnimationFrame(() => {
    node.style.height = `${node.scrollHeight}px`;
    node.style.opacity = "1";
  });
}

function onAfterEnter(el: Element) {
  const node = el as HTMLElement;
  node.style.height = "auto";
}

function onLeave(el: Element) {
  const node = el as HTMLElement;
  node.style.height = `${node.scrollHeight}px`;
  requestAnimationFrame(() => {
    node.style.height = "0px";
    node.style.opacity = "0";
  });
}
</script>

<template>
  <div v-if="categories.length" class="space-y-4">
    <div class="flex flex-wrap gap-2">
      <button
        v-for="category in categories"
        :key="category.key"
        class="px-3 py-2 rounded-full text-sm border"
        :class="
          (activeCategory?.key || categories[0]?.key) === category.key
            ? 'bg-gray-800 text-white border-gray-800 dark:bg-gray-200 dark:text-gray-900 dark:border-gray-200'
            : 'bg-white text-gray-700 border-gray-200 dark:bg-gray-900 dark:text-gray-200 dark:border-gray-700'
        "
        @click="setCategory(category.key)"
      >
        {{ category.label }} ({{ category.questions.length }})
      </button>
    </div>

    <div class="space-y-2">
      <article
        v-for="(item, index) in activeCategory?.questions ?? []"
        :key="item.q"
        class="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900/40"
      >
        <button
          class="w-full flex items-start justify-between gap-3 px-4 py-3 text-left"
          @click="toggleQuestion(index)"
        >
          <h3 class="text-sm font-medium text-gray-900 dark:text-gray-100">
            {{ item.q }}
          </h3>
          <span class="text-base leading-none text-gray-500 dark:text-gray-400 mt-0.5">
            {{ openQuestionIndex === index ? "-" : "+" }}
          </span>
        </button>

        <Transition
          name="accordion"
          @enter="onEnter"
          @after-enter="onAfterEnter"
          @leave="onLeave"
        >
          <div
            v-if="openQuestionIndex === index"
            class="overflow-hidden px-4 pb-3"
          >
            <p class="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
              {{ item.a }}
            </p>
          </div>
        </Transition>
      </article>
    </div>
  </div>
</template>

<style scoped>
.accordion-enter-active,
.accordion-leave-active {
  transition: height 0.2s ease, opacity 0.2s ease;
}
</style>
